<script setup lang="ts">
import AppState, {
  getCalendarsInLocation,
  getEquipmentsInLocation,
  getRoomsInLocation,
  getServiceMenusInLocation,
  getTeamsInLocation,
  isEquipmentEnabled,
  isRoomEnabled,
  isServiceCalendarEnabled,
  resetCalendarEditValidationData,
  setEditingCalendar,
  setIsLoading,
  updateCalendarStore,
} from '@/states/app'

import { CalendarList } from '@/class/pages/CalendarList'
import { GroupList } from '@/class/pages/GroupList'
import CalendarListTable from '@/components/Settings/CalendarListTable.vue'
import CreateCalendarModalWithSchedule from '@/components/Settings/CreateCalendarModalWithSchedule.vue'
import SelectCalendarTypeModalVue_V2 from '@/components/Settings/SelectCalendarTypeModalV2.vue'
import { UISpin, UITabPane, UITabs } from '@gohighlevel/ghl-ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import CalendarGroupList from '../components/Settings/CalendarGroupList.vue'
import CreateEquipmentModal from '../components/Settings/CreateEquipmentModal.vue'
import CreateRoomModal from '../components/Settings/CreateRoomModal.vue'
import EquipmentsListTable from '../components/Settings/EquipmentsListTable.vue'
import RoomsListTable from '../components/Settings/RoomsListTable.vue'
import ServicesListTable from '../components/Settings/ServicesListTable.vue'
import Calendar, { CalendarType } from '../models/calendar'

const showNewGroupModal = ref(false)
const showNewEquipmentModal = ref(false)
const showNewRoomModal = ref(false)
const showCalendarTypeModal = ref(false)
const showCreateCalendarModal = ref(false)
const selectedCalendarType = ref(CalendarType.EVENT)
const route = useRoute()
const tabType = ref(route.query?.tab || 'calendars')
const calendarList = new CalendarList()
const groupFilter = ref('')
const groupList = new GroupList('service')

onMounted(async () => {
  setIsLoading(true)
  await getTeamsInLocation()
  await getCalendarsInLocation()
  if (isServiceCalendarEnabled()) {
    await getServiceMenusInLocation()
  }
  if (isRoomEnabled()) {
    await getRoomsInLocation()
  }
  if (isEquipmentEnabled()) {
    await getEquipmentsInLocation()
  }
  setIsLoading(false)
})

const openClassBookingCalendarModal = () => {
  selectedCalendarType.value = CalendarType.CLASS_BOOKING
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openCollectiveBookingCalendarModal = () => {
  selectedCalendarType.value = CalendarType.COLLECTIVE
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openUnassignedCalendar = () => {
  selectedCalendarType.value = CalendarType.EVENT
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const openSelectTeamModal = (calendarType: CalendarType) => {
  selectedCalendarType.value = calendarType
  showCreateCalendarModal.value = true

  showCalendarTypeModal.value = false
}

const closeEquipmentModal = () => {
  showNewEquipmentModal.value = false
}

const newEquipmentCreated = () => {
  getEquipmentsInLocation()
  closeEquipmentModal()
}

const closeRoomModal = () => {
  showNewRoomModal.value = false
}

const openTeamCalendar = selectedGroupId => {
  showCreateCalendarModal.value = true
}

const openCalendarTypeModal = () => {
  resetCalendarEditValidationData()
  if (
    AppState.isCalendarTypeSplitEnabled ||
    AppState.isMergeCalendarTypeSetupEnabled
  ) {
    openSelectTeamModal(CalendarType.SERVICE)
    return
  }
  showCalendarTypeModal.value = true
}

const newRoomCreated = () => {
  getRoomsInLocation()
  closeRoomModal()
}

const openNewGroupModal = () => {
  showNewGroupModal.value = true
}

const newCalendarCreated = async (calendar: Calendar) => {
  showCreateCalendarModal.value = false
  AppState.showCalendarLinkModal = true
  setEditingCalendar(calendar)
  setIsLoading(true)
  await getCalendarsInLocation(true)
  setIsLoading(false)
  updateCalendarStore()
}

const showServiceMenuTab = computed(() => {
  return route.query?.tab === 'services' || isServiceCalendarEnabled()
})

const onGroupFilterChange = (newValue: string) => {
  groupFilter.value = newValue
}
</script>

<template>
  <div>
    <SelectCalendarTypeModalVue_V2
      :show="showCalendarTypeModal"
      :open-unassigned-calendar-modal="openUnassignedCalendar"
      :open-team-calendar-modal="openSelectTeamModal"
      :open-class-booking-calendar-modal="openClassBookingCalendarModal"
      :open-collective-booking-calendar-modal="
        openCollectiveBookingCalendarModal
      "
      @on-modal-close="() => (showCalendarTypeModal = false)"
    />

    <UITabs v-model:value="tabType" type="line">
      <UITabPane
        name="calendars"
        :tab="$t('settings.calendar.service_calendars')"
        class="mt-3"
      >
        <div class="flex flex-row gap-4">
          <div
            v-if="AppState.isLoading || AppState.loadingAssets.calendars"
            class="fixed inset-0 z-50 flex items-center justify-center"
          >
            <UISpin size="large" />
          </div>
          <div v-else class="flex w-full flex-row gap-7">
            <CalendarGroupList
              :group-filter="groupFilter"
              class="w-[250px]"
              :calendar-list="calendarList"
              type="service"
              @update:group-filter="onGroupFilterChange"
              :group-list="groupList"
            />
            <CalendarListTable
              :open-calendar-type-modal="openCalendarTypeModal"
              :open-new-group-modal="openNewGroupModal"
              :group-filter="groupFilter"
              class="w-full"
              :calendar-list="calendarList"
              type="service"
              @update:group-filter="onGroupFilterChange"
              :group-list="groupList"
            />
          </div>
        </div>
      </UITabPane>

      <UITabPane
        v-if="showServiceMenuTab"
        name="services"
        :tab="$t('settings.calendar.service_menus')"
        class="mt-3"
      >
        <ServicesListTable />
      </UITabPane>

      <UITabPane
        v-if="isRoomEnabled()"
        name="rooms"
        :tab="$t('settings.calendar.rooms')"
        class="mt-3"
      >
        <RoomsListTable />
      </UITabPane>

      <UITabPane
        v-if="isEquipmentEnabled()"
        name="equipments"
        :tab="$t('settings.calendar.equipments')"
        class="mt-3"
      >
        <EquipmentsListTable />
      </UITabPane>
    </UITabs>

    <CreateCalendarModalWithSchedule
      v-if="showCreateCalendarModal"
      :show="showCreateCalendarModal"
      :selected-calendar-type="selectedCalendarType"
      @on-modal-close="() => (showCreateCalendarModal = false)"
      @on-confirm="newCalendarCreated"
    />
    <CreateEquipmentModal
      :show="showNewEquipmentModal"
      @createdRecord="newEquipmentCreated"
      @closeModal="closeEquipmentModal"
    />
    <CreateRoomModal
      :show="showNewRoomModal"
      @createdRecord="newRoomCreated"
      @closeModal="closeRoomModal"
    />
  </div>
</template>
